import { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import authHeader from "services/auth-header";
import API from "services/axios";

/**
 * "Combine" — put several work orders onto one shipping address.
 *
 * The problem this solves is the one the whole module keeps running into: a
 * single physical dock arrives written several ways ("118 ACADEMY DR" from a
 * validator, "118 Academy Drive" typed by hand, the suite on a different
 * line), so it shows up as three accordions that a packer has to recognise as
 * the same place.
 *
 * Validating each group separately does NOT fix that — each address is
 * individually plausible, so UPS answers each one and they stay apart. What
 * merges them is picking ONE address and writing it to all of them.
 *
 * The list is the invoice's address groups, and under each group its work
 * orders. Ticks are PER WORK ORDER: tick a group's header to take every order
 * on it, or open it and tick just the stray one. Then choose which group's
 * address is the right one, and it is written to every ticked order.
 *
 * One list, two grains, because the two questions people bring here are the
 * same operation at different sizes:
 *
 *   - "these three addresses are the same place" -- tick three headers.
 *   - "put THIS order on the address the others use" -- tick one order.
 *   - "push a corrected address onto some orders that already share one" --
 *     a single group, tick the orders that need it.
 *
 * Until 2026-09-09 the first and the last were two separate button pairs
 * ("Combine" over groups, "Combine WOs" over a customer's orders) driving this
 * same modal with different wording. When every group held one order the two
 * pairs did identical things and sat side by side, which is how it was
 * reported. This is the collapse of the two.
 *
 * TWO MODES, because validating is a separate question from merging:
 *
 *   "validate" - check the chosen address with UPS first, then write it.
 *                Use when nobody is sure the address is right.
 *   "combine"  - write the chosen address as it stands, no UPS call. Use when
 *                the address is already known good (often it is the one UPS
 *                already validated, shown by the badge on its row) and the
 *                only problem is that the groups are split.
 *
 * Plain combine does NOT record a validation verdict. It clears the stored
 * one instead, because claiming UPS approved -- or rejected -- an address it
 * was never shown would put a badge on screen that nothing backs, and would
 * poison the cache so a later Validate press returned the invention instead
 * of asking.
 *
 * Only the POSTAL fields are written. addressCompany is not part of the
 * grouping key (see addressKeyOf in the API), so it does not need to match for
 * groups to merge — and stamping one group's company onto another customer's
 * order would relabel it for nothing. Each order keeps its own company and
 * attention-to; the API preserves them when the update omits them.
 */

const addressOneLine = (a) => {
  if (!a) return "—";
  const cityLine = [a.city, a.state, a.zip].filter(Boolean).join(" ");
  return [a.addressLine1, a.addressLine2, cityLine]
    .filter((l) => l && String(l).trim() !== "")
    .join(", ");
};

const groupLabel = (g) =>
  g?.label || g?.address?.addressCompany || g?.customerName || "Unnamed company";

const orderLabel = (o) =>
  `W-${o.woId}${o.jobName ? ` · ${o.jobName}` : ""}`;

/**
 * What UPS has said about this group's address, if anything.
 *
 * This is the whole point of showing it here: with plain Combine you are
 * choosing an address to stamp onto other people's orders WITHOUT checking it,
 * so which one UPS has already confirmed is exactly what you need to see to
 * choose well. "Checked" and "validated" are separate flags for a reason --
 * UPS answering is not UPS approving.
 */
const ValidationBadge = ({ group }) => {
  if (group?.addressValidated) {
    return (
      <span className="badge bg-success bg-opacity-75" title="UPS resolved this address">
        <i className="mdi mdi-check-circle-outline me-1" aria-hidden="true" />
        Validated
      </span>
    );
  }
  if (group?.addressChecked) {
    return (
      <span
        className="badge bg-warning text-dark"
        title="UPS was asked and could not resolve this address"
      >
        <i className="mdi mdi-alert-outline me-1" aria-hidden="true" />
        UPS could not resolve
      </span>
    );
  }
  return (
    <span className="badge bg-light text-muted border" title="Never checked with UPS">
      Not checked
    </span>
  );
};

// The group's orders with a usable woId, deduplicated. One order can only be
// on one group, but the flatten below is exactly where a repeat would slip
// through and be written twice.
const ordersOf = (g) => {
  const seen = new Set();
  const out = [];
  for (const o of g?.orders || []) {
    const woId = Number(o?.woId);
    if (!Number.isInteger(woId) || woId <= 0 || seen.has(woId)) continue;
    seen.add(woId);
    out.push({ ...o, woId });
  }
  return out;
};

// Whether this group has an address it can OFFER as the source. A work order
// with an empty shipping address (the "no address" group) can still be a
// TARGET -- it receives another group's address, which is the whole point of
// combining it -- but it has nothing to hand out, so it cannot be the source.
const canBeSource = (g) => {
  const a = g?.address;
  return !!(a && (a.addressLine1 || a.city || a.zip));
};

/**
 * A checkbox that can show the third state: some, not all, of a group's
 * orders ticked. `indeterminate` is a DOM property, not an attribute, so it
 * has to be set by hand.
 */
const TriStateCheckbox = ({ all, some, ...rest }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !all && some;
  }, [all, some]);
  return <input ref={ref} type="checkbox" checked={all} {...rest} />;
};

export default function CombineAddressesModal({
  groups,
  // "validate" (check with UPS first) or "combine" (write as-is). Defaults to
  // validate: the more cautious of the two is the safer thing to fall back to.
  mode = "validate",
  onHide,
  onApplied,
}) {
  const list = useMemo(() => (Array.isArray(groups) ? groups : []), [groups]);
  const validateFirst = mode !== "combine";

  // Nothing ticked to start with. Combining rewrites live shipping addresses,
  // so which orders are the same place is a judgement the user makes — not
  // something to pre-tick and have them untick.
  const [ticked, setTicked] = useState({}); // woId -> true
  const [sourceKey, setSourceKey] = useState(null);
  // Groups the user has opened to tick individual orders. Single-order groups
  // never need opening: their header IS the order.
  const [open, setOpen] = useState({});

  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState(null); // the validated address + meta
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);

  const source = list.find((g) => g.key === sourceKey) || null;
  const sourceWoIds = new Set(ordersOf(source).map((o) => o.woId));
  const targetWoIds = list
    .flatMap(ordersOf)
    .map((o) => o.woId)
    .filter((id) => ticked[id]);
  const totalOrders = list.reduce((n, g) => n + ordersOf(g).length, 0);
  const groupsTouched = list.filter((g) =>
    ordersOf(g).some((o) => ticked[o.woId]),
  );

  // Ticked orders that are NOT already on the source address. In combine
  // mode these are the only ones anything happens to -- writing an address
  // onto the orders it came from changes nothing -- so with none of them the
  // button would save nothing and say it saved. In validate mode UPS may
  // return a corrected form, which the source's own orders should get too, so
  // there any ticked order counts.
  const movingWoIds = targetWoIds.filter((id) => !sourceWoIds.has(id));
  const canProceed =
    !!source && (validateFirst ? targetWoIds.length : movingWoIds.length) >= 1;

  // What gets written. In validate mode that is UPS's answer (or the address
  // as it stands when UPS could not resolve it); in combine mode it is simply
  // the chosen group's address, untouched.
  const applyAddress = validateFirst ? checked?.address : source?.address;

  // Any change to the selection invalidates a check made for the old one.
  const invalidate = () => {
    setChecked(null);
    setConfirming(false);
  };

  const toggleOrder = (woId) => {
    setTicked((prev) => ({ ...prev, [woId]: !prev[woId] }));
    invalidate();
  };

  // Header tick: everything on, unless everything already is, then off.
  const toggleGroup = (g) => {
    const ids = ordersOf(g).map((o) => o.woId);
    const allOn = ids.length > 0 && ids.every((id) => ticked[id]);
    setTicked((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = !allOn;
      });
      return next;
    });
    invalidate();
  };

  // Choosing a source with none of its orders ticked ticks them all: the
  // address is going to be written across the selection, and the orders it
  // came from are part of that selection in every real case (and get UPS's
  // corrected form in validate mode). They stay untickable by hand.
  const chooseSource = (g) => {
    setSourceKey(g.key);
    const ids = ordersOf(g).map((o) => o.woId);
    if (!ids.some((id) => ticked[id])) {
      setTicked((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          next[id] = true;
        });
        return next;
      });
    }
    invalidate();
  };

  /**
   * Validate the chosen address only — one UPS call, not one per order.
   *
   * force, deliberately: the address about to be stamped onto other people's
   * orders should be checked now, not read from a record that may predate an
   * edit.
   */
  const check = async () => {
    const woId = ordersOf(source)[0]?.woId;
    if (!woId) return;
    setChecking(true);
    setError(null);
    try {
      const { data } = await API.post(
        "/bundle-sync/validate-addresses-preview",
        { woIds: [woId], force: true },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Validation failed");
      const row = (data.data || [])[0];
      if (!row) throw new Error("UPS returned nothing for this address");
      setChecked({
        ok: !!row.ok,
        reason: row.reason || null,
        // What actually gets written: UPS's form when it gave one, otherwise
        // the address exactly as it stands.
        address: row.ok && row.suggested ? row.suggested : row.current,
        changed: !!row.changed,
      });
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
      setChecked(null);
    }
    setChecking(false);
  };

  const apply = async () => {
    if (!applyAddress) return;
    const a = applyAddress;
    // Postal fields ONLY. addressCompany and attentionTo are left out so each
    // work order keeps its own -- see the note at the top of this file.
    const address = {
      addressLine1: a.addressLine1 || "",
      addressLine2: a.addressLine2 || "",
      city: a.city || "",
      state: a.state || "",
      zip: a.zip || "",
      country: a.country || "US",
    };
    setApplying(true);
    setError(null);
    try {
      const { data } = await API.post(
        "/bundle-sync/validate-addresses-apply",
        {
          updates: targetWoIds.map((woId) => ({
            woId,
            address,
            // Three-state, matching the API:
            //   true  - UPS resolved it
            //   false - UPS was asked and could not resolve it
            //   null  - never asked (plain combine), so the stored verdict is
            //           CLEARED rather than invented
            validated: validateFirst ? !!checked.ok : null,
            // Recorded in the order's address history. Which flow made the
            // change is worth keeping: "combine" means nobody checked this
            // address, "validate" means UPS was asked -- different answers to
            // "why is this parcel at the wrong door".
            source: validateFirst ? "validate" : "combine",
          })),
        },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Could not apply");
      onApplied(data.appliedCount || 0, data.skipped || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
      setApplying(false);
    }
  };

  const whyNot = !source
    ? "Choose which address to use"
    : !canProceed
      ? validateFirst
        ? "Tick at least one work order"
        : "Tick at least one work order that is not already on this address"
      : undefined;

  // "address" is the one word here whose plural is not word + "s".
  const plural = (n, word) =>
    `${n} ${n === 1 ? word : word === "address" ? "addresses" : `${word}s`}`;

  return (
    <>
      <style>
        {`
          .ns-combine-backdrop { z-index: 100050; }
          .ns-combine-modal    { z-index: 100051; }
          .ns-combine-order:hover { background: rgba(0,0,0,.03); }
        `}
      </style>

      <Modal
        show
        onHide={applying ? undefined : onHide}
        size="lg"
        scrollable
        centered
        backdropClassName="ns-combine-backdrop"
        className="ns-combine-modal"
        contentClassName="shadow-lg"
      >
        <Modal.Header closeButton={!applying}>
          <Modal.Title className="h5 mb-0">
            {validateFirst ? "Validate & Combine addresses" : "Combine addresses"}
            <span className="text-muted fs-6 ms-2">
              {plural(list.length, "address")} · {plural(totalOrders, "work order")}
            </span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && <div className="alert alert-danger py-2 px-3">{error}</div>}

          <p className="text-muted" style={{ fontSize: 14 }}>
            Tick the work orders that go to the{" "}
            <strong>same physical place</strong> — a whole address at once, or
            open one and tick just the orders you mean — then choose which
            address is the correct one.{" "}
            {validateFirst ? (
              <>It is checked with UPS and written to every ticked order.</>
            ) : (
              <>
                It is written to every ticked order <strong>as it stands</strong>.
                UPS is not called — the badges show what has already been
                checked, so you can pick one UPS has confirmed.
              </>
            )}
          </p>

          <div className="border rounded">
            {list.map((g, i) => {
              const orders = ordersOf(g);
              const onCount = orders.filter((o) => ticked[o.woId]).length;
              const all = orders.length > 0 && onCount === orders.length;
              const some = onCount > 0;
              const sourceable = canBeSource(g);
              const expandable = orders.length > 1;
              const isOpen = expandable && !!open[g.key];
              return (
                <div key={g.key} className={i ? "border-top" : ""}>
                  <div
                    className={`d-flex align-items-start gap-2 p-2 ${
                      some ? "bg-primary bg-opacity-10" : ""
                    }`}
                  >
                    <TriStateCheckbox
                      all={all}
                      some={some}
                      className="form-check-input mt-1"
                      disabled={applying || orders.length === 0}
                      onChange={() => toggleGroup(g)}
                      aria-label={`Include every work order at ${groupLabel(g)}`}
                    />
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="fw-semibold" style={{ fontSize: 15 }}>
                        {groupLabel(g)}
                      </div>
                      <div style={{ fontSize: 14 }}>
                        {sourceable ? (
                          addressOneLine(g.address)
                        ) : (
                          <span className="text-muted fst-italic">
                            no address yet — tick to give it one
                          </span>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                        {sourceable && <ValidationBadge group={g} />}
                        {expandable ? (
                          // Opens the per-order list. A link rather than a
                          // chevron: "2 of 5 work orders" is the thing worth
                          // clicking, and reads as a count when closed.
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            style={{ fontSize: 13 }}
                            onClick={() =>
                              setOpen((p) => ({ ...p, [g.key]: !p[g.key] }))
                            }
                            aria-expanded={isOpen}
                          >
                            <i
                              className={`mdi ${
                                isOpen ? "mdi-chevron-down" : "mdi-chevron-right"
                              }`}
                              aria-hidden="true"
                            />
                            {some && !all ? `${onCount} of ` : ""}
                            {plural(orders.length, "work order")}
                          </button>
                        ) : (
                          <span className="text-muted" style={{ fontSize: 13 }}>
                            {orders.length ? orderLabel(orders[0]) : "no work order"}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Any group with an address of its own can be the
                        source. A no-address group is only ever a TARGET --
                        it is what receives the chosen address -- so its
                        radio is off. */}
                    <div className="text-nowrap">
                      <label
                        className={`d-flex align-items-center gap-1 ${
                          sourceable ? "" : "text-muted"
                        }`}
                        style={{ fontSize: 13 }}
                        title={
                          sourceable
                            ? "Write this address onto the ticked work orders"
                            : "This work order has no address to offer — it can only receive one"
                        }
                      >
                        <input
                          type="radio"
                          name="ns-combine-source"
                          className="form-check-input mt-0"
                          checked={sourceKey === g.key}
                          disabled={!sourceable || applying}
                          onChange={() => chooseSource(g)}
                        />
                        {sourceable ? "Use this one" : "Receives"}
                      </label>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="ps-4 pb-1">
                      {orders.map((o) => (
                        <label
                          key={o.woId}
                          className="ns-combine-order d-flex align-items-center gap-2 px-2 py-1 rounded"
                          style={{ fontSize: 14, cursor: "pointer" }}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input mt-0"
                            checked={!!ticked[o.woId]}
                            disabled={applying}
                            onChange={() => toggleOrder(o.woId)}
                          />
                          <span>{orderLabel(o)}</span>
                          {o.shipped && (
                            <span className="badge bg-success bg-opacity-75">
                              Shipped
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {source && !canProceed && (
            <div className="text-muted mt-2" style={{ fontSize: 14 }}>
              {whyNot}.
            </div>
          )}

          {validateFirst && checked && (
            <div
              className={`alert py-2 px-3 mt-3 mb-0 ${
                checked.ok ? "alert-success" : "alert-warning"
              }`}
            >
              <div className="fw-semibold mb-1">
                {checked.ok
                  ? checked.changed
                    ? "UPS returned a corrected form of this address"
                    : "UPS confirmed this address"
                  : "UPS could not resolve this address"}
              </div>
              <div style={{ fontSize: 14 }}>{addressOneLine(checked.address)}</div>
              {!checked.ok && (
                <div className="mt-1" style={{ fontSize: 13 }}>
                  {checked.reason} — it can still be applied as written, which
                  merges the orders, but nobody has confirmed it is deliverable.
                </div>
              )}
            </div>
          )}

          {/* The prompt. Deliberately explicit and deliberately last: this
              rewrites the shipping address on live orders, which is what
              prints on PDFs and parcel labels. */}
          {confirming && applyAddress && (
            <div className="alert alert-warning py-2 px-3 mt-3 mb-0">
              <div className="fw-semibold mb-1">
                This address will be saved to {plural(targetWoIds.length, "work order")}:
              </div>
              <div className="mb-2" style={{ fontSize: 14 }}>
                {addressOneLine(applyAddress)}
              </div>
              <ul className="mb-2 ps-3" style={{ fontSize: 13 }}>
                {groupsTouched.map((g) => (
                  <li key={g.key}>
                    <strong>{groupLabel(g)}</strong> — W-
                    {ordersOf(g)
                      .filter((o) => ticked[o.woId])
                      .map((o) => o.woId)
                      .join(", W-")}
                    {g.key === sourceKey ? " (source)" : ""}
                  </li>
                ))}
              </ul>
              <div style={{ fontSize: 13 }}>
                Their street, city, state and ZIP are replaced, so they land on
                one address. Each order keeps its own company name and
                attention-to. This is what prints on labels and PDFs.
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onHide}
            disabled={applying}
          >
            Cancel
          </button>

          {validateFirst && !checked && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={check}
              disabled={!canProceed || checking}
              title={whyNot || "Check the chosen address with UPS"}
            >
              {checking ? "Checking with UPS…" : "Validate chosen address"}
            </button>
          )}

          {applyAddress && !confirming && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setConfirming(true)}
              disabled={!canProceed}
              title={whyNot}
            >
              Apply to {plural(targetWoIds.length, "work order")}…
            </button>
          )}

          {applyAddress && confirming && (
            <button
              type="button"
              className="btn btn-warning"
              onClick={apply}
              disabled={applying}
            >
              {applying
                ? "Saving…"
                : `Yes, save to ${plural(targetWoIds.length, "work order")}`}
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}
